import React from 'react';

import { setHours, setMinutes, setSeconds } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import { formatDate } from 'lib/helpers/dateUtils';
import { ia } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';

import Icon from 'components/shared/Icon/Icon';
import TimePicker from 'components/shared/TimePicker/TimePicker';
import Header from 'components/shared/shared/Header';

const TimeSelector = ({
  isTime = false,
  isTimeRange = false,
  isDateRange = false,
  value,
  onChange,
  baseDate = null // The date from DateSelector to combine with time
}) => {
  const timezone = useTimezone();

  if (!isTime && !isTimeRange) return null;

  const initialStart = setSeconds(setMinutes(setHours(new Date(), 0), 0), 0);
  const initialEnd = setSeconds(setMinutes(setHours(new Date(), 23), 59), 0);

  if (isTimeRange || isDateRange) {
    const startDate = new Date(value?.[0]);
    const endDate = new Date(value?.[1]);

    const startTimeValue = value?.[0]
      ? formatDate(startDate, timezone, 'HH:mm')
      : formatDate(initialStart, timezone, 'HH:mm');
    const endTimeValue = value?.[1]
      ? formatDate(endDate, timezone, 'HH:mm')
      : formatDate(initialEnd, timezone, 'HH:mm');

    const handleStartTimeChange = (newTime) => {
      if (!newTime) {
        onChange([null, value?.[1] || null]);
        return;
      }

      const baseDateTime = ia(baseDate) ? baseDate?.[0] : baseDate || new Date();
      const [hours, minutes] = newTime.split(':').map(Number);
      const baseDateInTz = toZonedTime(new Date(baseDateTime), timezone);
      const newDateZoned = setMinutes(setHours(baseDateInTz, hours), minutes);

      onChange([fromZonedTime(newDateZoned, timezone), value?.[1] || null]);
    };

    const handleEndTimeChange = (newTime) => {
      if (!newTime) {
        onChange([value?.[0] || null, null]);
        return;
      }

      const baseDateTime = ia(baseDate) ? baseDate?.[1] : baseDate || new Date();
      const [hours, minutes] = newTime.split(':').map(Number);
      const baseDateInTz = toZonedTime(new Date(baseDateTime), timezone);
      const newDateZoned = setMinutes(setHours(baseDateInTz, hours), minutes);

      onChange([value?.[0] || null, fromZonedTime(newDateZoned, timezone)]);
    };

    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-end gap-1">
          <div className="flex w-full flex-col">
            <Header label="Start time" required />
            <TimePicker
              value={startTimeValue}
              onChange={handleStartTimeChange}
              hasOptions={false}
              options={null}
            />
          </div>
          <div className="flex min-w-[28px] items-center justify-center">
            <Icon
              icon="minus-linear"
              color="neutral"
              size={20}
              shade={700}
              className="!mb-[10px]"
            />
          </div>
          <div className="flex w-full flex-col">
            <Header label="End time" required />
            <TimePicker value={endTimeValue} onChange={handleEndTimeChange} hasOptions={false} />
          </div>
        </div>
      </div>
    );
  }

  if (isTime) {
    // Extract time from value or baseDate
    const date = new Date(value);
    const timeValue = value
      ? formatDate(date, timezone, 'HH:mm')
      : formatDate(initialStart, timezone, 'HH:mm');

    const handleTimeChange = (newTime) => {
      if (!newTime) {
        onChange(null);
        return;
      }

      const baseDateTime = baseDate || new Date();
      const [hours, minutes] = newTime.split(':').map(Number);
      const baseDateInTz = toZonedTime(new Date(baseDateTime), timezone);
      const newDateZoned = setMinutes(setHours(baseDateInTz, hours), minutes);

      onChange(fromZonedTime(newDateZoned, timezone));
    };

    return (
      <TimePicker
        value={timeValue}
        required
        label="Time"
        hasOptions={false}
        options={null}
        onChange={handleTimeChange}
      />
    );
  }

  return null;
};

export default TimeSelector;
